/**
 * Local wall-clock birth time → UTC.
 *
 * This is the single largest source of silently-wrong birth charts, so every
 * ambiguous case is surfaced rather than guessed. Three traps are handled
 * explicitly:
 *
 *  1. Non-existent times (the spring-forward gap) — no UTC instant maps to the
 *     stated wall clock, so we return an error instead of inventing one.
 *  2. Ambiguous times (the fall-back repeated hour) — two instants map to the
 *     same wall clock, so we return both and ask.
 *  3. Pre-standard-time births using Local Mean Time. tzdata maps ALL of India
 *     to Asia/Kolkata, so any birth before 1906 returns Calcutta's LMT
 *     (+05:21:10) regardless of the actual city. For Porbandar that is a
 *     43-minute error — roughly 24 arcminutes of Moon and 11 degrees of
 *     Ascendant, and completely invisible. We detect it and require an explicit
 *     choice from the user.
 *
 * Offsets come from the IANA tz database via Intl, which ships full historical
 * transition rules — India's 1942–45 wartime DST included. Do not hand-roll a
 * DST table.
 */

export interface WallClock {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number; // 0-23
  minute: number;
  second?: number;
}

export type ResolvedTime =
  | { ok: true; utc: Date; offsetMinutes: number; lmt: false }
  | { ok: false; reason: 'nonexistent'; message: string }
  | { ok: false; reason: 'ambiguous'; message: string; candidates: { utc: Date; offsetMinutes: number }[] }
  | { ok: false; reason: 'lmt'; message: string; offsetMinutes: number; suggestedLmtOffsetMinutes?: number };

/** Offset of `zone` at instant `date`, in minutes east of UTC. */
export function zoneOffsetMinutes(date: Date, zone: string): number {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    timeZoneName: 'longOffset',
  });
  const part = fmt.formatToParts(date).find((p) => p.type === 'timeZoneName');
  return parseOffset(part?.value ?? 'GMT+00:00');
}

/**
 * Parses "GMT+05:30", "GMT+05:21:10", "GMT-8", "GMT" into minutes east of UTC.
 * The seconds component is significant: its presence is how we detect LMT.
 */
export function parseOffset(value: string): number {
  const m = /GMT([+-])(\d{1,2})(?::(\d{2}))?(?::(\d{2}))?/.exec(value);
  if (!m) return 0; // bare "GMT"
  const sign = m[1] === '-' ? -1 : 1;
  const h = Number(m[2]);
  const min = Number(m[3] ?? 0);
  const sec = Number(m[4] ?? 0);
  return sign * (h * 60 + min + sec / 60);
}

/** True when the zone offset carries a non-zero seconds component (i.e. LMT). */
export function offsetHasSeconds(date: Date, zone: string): boolean {
  const fmt = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'longOffset' });
  const value = fmt.formatToParts(date).find((p) => p.type === 'timeZoneName')?.value ?? '';
  return /GMT[+-]\d{1,2}:\d{2}:\d{2}/.test(value);
}

/** Renders an instant back into `zone` as wall-clock components. */
function wallClockIn(date: Date, zone: string): WallClock {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hourCycle: 'h23',
  });
  const p: Record<string, string> = {};
  for (const part of fmt.formatToParts(date)) p[part.type] = part.value;
  return {
    year: Number(p.year), month: Number(p.month), day: Number(p.day),
    hour: Number(p.hour), minute: Number(p.minute), second: Number(p.second),
  };
}

function sameWallClock(a: WallClock, b: WallClock): boolean {
  return a.year === b.year && a.month === b.month && a.day === b.day
    && a.hour === b.hour && a.minute === b.minute;
}

/** The wall clock treated as if it were UTC — the starting guess. */
function asUtc(w: WallClock): Date {
  return new Date(Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second ?? 0));
}

/**
 * Resolves a local wall-clock birth time to a UTC instant.
 *
 * @param wall  the time as written on the birth certificate
 * @param zone  IANA zone id, e.g. "Asia/Kolkata"
 * @param longitudeDeg  birth longitude, used only to suggest a true LMT offset
 */
export function resolveBirthTime(
  wall: WallClock,
  zone: string,
  longitudeDeg?: number,
): ResolvedTime {
  const guess = asUtc(wall);

  /* Collect every offset the zone uses in a ±1 day window around the guess.
     Probing only at the guess is not enough: on a fall-back date both the
     pre- and post-transition offsets produce a valid instant for the same wall
     clock, and one of them is only observable on the far side of the
     transition. A day's window comfortably brackets any real transition. */
  const DAY = 86_400_000;
  const offsets = new Set<number>();
  for (const probe of [guess.getTime() - DAY, guess.getTime(), guess.getTime() + DAY]) {
    offsets.add(zoneOffsetMinutes(new Date(probe), zone));
  }

  /* Keep only candidates that round-trip: formatting the instant back in
     `zone` must reproduce exactly the wall clock we were given. Zero survivors
     means the time never existed; two means it happened twice. */
  const seen = new Set<number>();
  const valid: { utc: Date; offsetMinutes: number }[] = [];
  for (const off of offsets) {
    const cand = new Date(guess.getTime() - off * 60_000);
    if (seen.has(cand.getTime())) continue;
    seen.add(cand.getTime());
    if (sameWallClock(wallClockIn(cand, zone), wall)) {
      valid.push({ utc: cand, offsetMinutes: zoneOffsetMinutes(cand, zone) });
    }
  }
  valid.sort((a, b) => a.utc.getTime() - b.utc.getTime());

  if (valid.length === 0) {
    return {
      ok: false,
      reason: 'nonexistent',
      message:
        'That local time does not exist on that date — the clocks moved forward across it. Please check the birth time.',
    };
  }

  if (valid.length > 1) {
    return {
      ok: false,
      reason: 'ambiguous',
      message:
        'That local time occurred twice on that date, because the clocks moved back. We need to know which one to give you an accurate chart.',
      candidates: valid,
    };
  }

  const resolved = valid[0]!;

  /* LMT trap. A non-zero seconds component means tzdata is returning local
     mean time for the zone's reference city, which is almost certainly not the
     birth city. Refuse rather than silently apply the wrong meridian. */
  if (offsetHasSeconds(resolved.utc, zone)) {
    return {
      ok: false,
      reason: 'lmt',
      message:
        'Births before standard time was adopted used local mean time, which varied by city. ' +
        'The timezone database only knows the reference city for this zone, so we cannot assume it matches the birth place. ' +
        'Please confirm the UTC offset, or use local mean time calculated from the birth longitude.',
      offsetMinutes: resolved.offsetMinutes,
      ...(longitudeDeg !== undefined
        ? { suggestedLmtOffsetMinutes: longitudeDeg * 4 } // 1° = 4 minutes of time
        : {}),
    };
  }

  return { ok: true, utc: resolved.utc, offsetMinutes: resolved.offsetMinutes, lmt: false };
}

/**
 * Resolves using an explicit UTC offset, bypassing the tz database entirely.
 * Used when the user answers an ambiguity or supplies an LMT/manual offset.
 */
export function resolveWithOffset(wall: WallClock, offsetMinutes: number): Date {
  return new Date(asUtc(wall).getTime() - offsetMinutes * 60_000);
}

/** True local mean time offset for a longitude, in minutes east of UTC. */
export function lmtOffsetMinutes(longitudeDeg: number): number {
  return longitudeDeg * 4;
}

/**
 * Verifies the runtime actually ships historical tz rules. Some stripped-ICU
 * builds do not, and would silently return the modern offset for a 1943 birth.
 * When this returns false the UI must fall back to manual offset entry rather
 * than producing a confidently wrong chart.
 */
export function hasHistoricalTzData(): boolean {
  try {
    // India observed +06:30 during the Second World War.
    const probe = new Date(Date.UTC(1943, 4, 15, 6, 0, 0));
    return Math.round(zoneOffsetMinutes(probe, 'Asia/Kolkata')) === 390;
  } catch {
    return false;
  }
}
